import { Fragment, useCallback } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import {
    CsvExportStrategy,
    JsonExportStrategy,
    triggerDownload,
} from '@/lib/export';
import type { ColumnDefinition } from '@/lib/export';

export interface DownloadMenuProps {
    /** Data array to export */
    data: unknown[];
    /** Column definitions for headers and formatting */
    columns: ColumnDefinition[];
    /** Base filename without extension */
    filename: string;
    /** Explicitly disable the menu */
    disabled?: boolean;
    /** Additional CSS classes for the button */
    className?: string;
}

/**
 * Reusable download menu component with CSV and JSON export options.
 *
 * Uses HeadlessUI Menu component for accessible dropdown.
 * Automatically disabled when data array is empty.
 *
 * @example
 * ```tsx
 * <DownloadMenu
 *   data={chartData}
 *   columns={[
 *     { key: 'date', label: 'Date', format: 'date' },
 *     { key: 'value', label: 'Value', format: 'money' },
 *   ]}
 *   filename="net-worth-2026-01-11"
 * />
 * ```
 */
export function DownloadMenu({
    data,
    columns,
    filename,
    disabled = false,
    className,
}: DownloadMenuProps) {
    // Auto-disable when no data
    const isDisabled = disabled || data.length === 0;

    const handleDownloadCsv = useCallback(() => {
        const strategy = new CsvExportStrategy();
        const blob = strategy.export(data, columns);
        triggerDownload(blob, `${filename}.csv`);
    }, [data, columns, filename]);

    const handleDownloadJson = useCallback(() => {
        const strategy = new JsonExportStrategy();
        const blob = strategy.export(data, columns);
        triggerDownload(blob, `${filename}.json`);
    }, [data, columns, filename]);

    return (
        <Menu as="div" className="relative">
            <Menu.Button
                disabled={isDisabled}
                className={`inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-secondary transition-colors ${
                    isDisabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-100 dark:hover:bg-dark cursor-pointer'
                } ${className || ''}`}
                title={isDisabled ? 'No data to download' : 'Download data'}
            >
                <ArrowDownTrayIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" aria-hidden="true" />
                <span className="sr-only">Download</span>
            </Menu.Button>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="absolute right-0 mt-2 w-40 origin-top-right rounded-lg border border-gray-200 bg-white shadow-lg focus:outline-hidden dark:border-gray-600 dark:bg-dark z-50">
                    <Menu.Item>
                        {({ active }) => (
                            <button
                                onClick={handleDownloadCsv}
                                className={`${
                                    active
                                        ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                                        : 'text-gray-700 dark:text-gray-200'
                                } w-full rounded-t-lg px-4 py-2 text-left text-sm cursor-pointer`}
                            >
                                Download CSV
                            </button>
                        )}
                    </Menu.Item>
                    <Menu.Item>
                        {({ active }) => (
                            <button
                                onClick={handleDownloadJson}
                                className={`${
                                    active
                                        ? 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                                        : 'text-gray-700 dark:text-gray-200'
                                } w-full rounded-b-lg px-4 py-2 text-left text-sm cursor-pointer`}
                            >
                                Download JSON
                            </button>
                        )}
                    </Menu.Item>
                </Menu.Items>
            </Transition>
        </Menu>
    );
}
